import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import LegacyLocalizationBridge from "../components/LegacyLocalizationBridge";
import { translateLegacyText } from "../i18n/legacyTranslation";

export const LANGUAGES = Object.freeze({
  ENGLISH: "en",
  GREEK: "el",
});

const STORAGE_KEY = "pythia.language";

const EN = {
  "file.saved": "Saved ✓",
  "file.savedAs": "Saved as “{{name}}” ✓",
  "file.deleted": "Project deleted",
  "file.discard": "You have unsaved changes. Discard them?",
  "file.discardImport": "You have unsaved changes. Discard them and import this project file?",
  "file.deleteUnsaved": "You have unsaved changes. Delete the project anyway?",
  "file.confirmDelete": "Delete project “{{name}}”? This cannot be undone.",
  "file.untitled": "Untitled",
  "file.saveAsTitle": "Save As",
  "file.newName": "New project name",
  "file.imported": "Imported “{{name}}”. Use Save As to save it to your account.",
  "file.loaded": "Project loaded",

  "language.name.en": "English",
  "language.name.el": "Greek",
  "language.switchToEnglish": "Switch to English",
  "language.switchToGreek": "Switch to Greek",
  "language.selector": "Application language",
  "app.version": "version",
  "app.test": "TEST",
  "app.project": "Project:",
  "app.daysLeft": "Days left: {{count}}",
  "usage.title": "Account usage",
  "usage.projects": "Projects {{used}}/{{limit}}",
  "usage.projectsNoLimit": "Projects {{used}}",
  "usage.simulationsToday": "Simulations today {{used}}/{{limit}}",
  "usage.simulationsTodayNoLimit": "Simulations today {{used}}",
  "usage.optimizationsToday": "Optimisations today {{used}}/{{limit}}",
  "usage.optimizationsTodayNoLimit": "Optimisations today {{used}}",
  "usage.projectsHint": "Projects saved in your account. Deleting a project frees a place.",
  "usage.projectsLimitHint": "Your account allows up to {{limit}} saved projects.",
  "usage.simulationsHint": "Simulations run today. Failed or cancelled runs do not count, and the count starts again every day.",
  "usage.simulationsLimitHint": "Your account allows up to {{limit}} simulations a day.",
  "usage.optimizationsHint": "Market optimisations run today, counted the same way.",
  "usage.optimizationsLimitHint": "Your account allows up to {{limit}} optimisations a day.",
  "usage.limitReached": "A limit has been reached.",
  "usage.panel.subtitle": "What your account allows and how much of it is in use.",
  "usage.panel.projects": "Saved projects",
  "usage.panel.simulations": "Simulations today",
  "usage.panel.optimizations": "Market optimisations today",
  "usage.panel.ofLimit": "{{used}} of {{limit}}",
  "usage.panel.noLimit": "{{used}} · no limit",
  "usage.panel.projectsLeft": "You can save {{count}} more",
  "usage.panel.runsLeft": "{{count}} left today",
  "usage.panel.runsLeftOne": "1 left today",
  "usage.panel.full": "Limit reached",
  "usage.panel.projectsHint": "Deleting a project frees a place.",
  "usage.panel.simulationsHint": "Failed or cancelled runs do not count; the count starts again every day.",
  "usage.panel.optimizationsHint": "Counted the same way as simulations.",
  "announcements.title": "Notifications",
  "announcements.newOne": "1 new",
  "announcements.newMany": "{{count}} new",
  "announcements.newBadge": "New",
  "announcements.empty": "No notifications at the moment.",
  "announcements.until": "Until {{time}} (Greek time)",
  "announcements.kind.info": "Information",
  "announcements.kind.maintenance": "Maintenance",
  "announcements.kind.important": "Important",
  "menu.file": "File",
  "menu.edit": "Edit",
  "menu.view": "View",
  "menu.help": "Help",
  "nav.menu": "Menu",
  "nav.projectSetup": "Project Setup",
  "nav.project": "Project",
  "nav.overviewAssets": "Overview & Assets",
  "nav.assetSetup": "Asset Setup",
  "nav.operationsDesk": "Operations Desk",
  "nav.trading": "Trading",
  "nav.positions": "Positions",
  "nav.orders": "Orders",
  "nav.reserves": "Reserves",
  "nav.optimisation": "Optimisation",
  "nav.passPlanner": "Pass Planner",
  "nav.reporting": "Reporting",
  "nav.settlementPerformance": "Settlement & Performance",
  "nav.inputsForecasts": "Inputs & Forecasts",
  "nav.setup": "Setup",
  "nav.portfolioOnboarding": "Portfolio & Onboarding",
  "nav.participationSettings": "Participation Settings",
  "nav.siteGeneral": "Site & General",
  "nav.pvSystem": "PV System",
  "nav.windSystem": "Wind System",
  "nav.bess": "BESS",
  "nav.gridConnection": "Grid Connection",
  "nav.dataForecasts": "Data & Forecasts",
  "nav.weatherGeneration": "Weather & Generation",
  "nav.marketData": "Market Data",
  "nav.marketOperations": "Market Operations",
  "nav.operations": "Operations",
  "nav.assetMonitoring": "Asset Monitoring",
  "nav.monitoringPortfolio": "Portfolio",
  "nav.monitoringAlarms": "Alarms",
  "nav.monitoringReports": "Monthly Report",
  "nav.monitoringSources": "Data Sources",
  "nav.dailyOptimization": "Daily Optimization",
  "nav.historicRuns": "Historic Runs",
  "nav.bidSchedules": "Bid Schedules",
  "nav.feasibilityStudy": "Feasibility Study",
  "nav.studyInfo": "Study Info",
  "nav.weatherInput": "Weather Data",
  "nav.technicalAssumptions": "Technical Assumptions",
  "nav.financialAssumptions": "Financial Assumptions",
  "nav.simulationReports": "Simulation & Reports",
  "nav.thirdPartyNotices": "Third-Party Software Notices",
  "action.new": "New",
  "action.open": "Open…",
  "action.importProject": "Import Project…",
  "action.save": "Save",
  "action.saving": "Saving…",
  "action.saveAs": "Save As…",
  "action.exportProject": "Export Project",
  "action.exportProjectJson": "Export Project JSON",
  "action.deleteProject": "Delete project…",
  "action.undo": "Undo",
  "action.redo": "Redo",
  "action.cancel": "Cancel",
  "action.close": "Close",
  "action.delete": "Delete",
  "action.search": "Search",
  "action.learnMore": "Learn more",
  "file.useSaveAs": "Use Save As first",
  "file.noChanges": "No changes",
  "file.noProject": "No project opened",
  "file.adminOnly": "Admin only · unencrypted",
  "view.darkMode": "Dark mode",
  "view.advancedOptions": "Advanced options",
  "view.tooltips": "Tooltips",
  "help.documentation": "Documentation",
  "help.manualBtm": "User manual: behind the meter (Greek, PDF)",
  "help.manualMerchant": "User manual: energy market / merchant (Greek, PDF)",
  "help.glossary": "Glossary",
  "manualLink.label": "Guide",
  "manualLink.tipBtm": "Open the user manual (behind the meter) at page {{page}}",
  "manualLink.tipMerchant": "Open the user manual (energy market) at page {{page}}",
  "glossary.search": "Search terms",
  "glossary.placeholder": "e.g. net billing, self-consumption, DSCR",
  "glossary.topics": "Topics",
  "glossary.group.all": "All",
  "glossary.group.btm": "Self-consumption",
  "glossary.group.pv": "PV and battery",
  "glossary.group.market": "Market and grid",
  "glossary.group.finance": "Economics and financing",
  "glossary.empty": "No term found. Try another word, in Greek or English.",
  "glossary.manualsHint": "More in the user manuals (Help menu).",
  "glossary.close": "Close",
  "report.title": "Report a problem / Feedback",
  "report.intro": "Describe what happened. The details we need to look into it are added automatically below, so you can see exactly what will be sent.",
  "report.kindLabel": "Type",
  "report.kind.problem": "Problem",
  "report.kind.suggestion": "Suggestion",
  "report.kind.question": "Question",
  "report.descriptionLabel.problem": "What happened?",
  "report.descriptionLabel.suggestion": "What would you like to see?",
  "report.descriptionLabel.question": "Your question",
  "report.descriptionPlaceholder.problem": "What you did, what you expected and what happened instead.",
  "report.descriptionPlaceholder.suggestion": "Describe the idea and how it would help your work.",
  "report.descriptionPlaceholder.question": "Write your question as fully as you can.",
  "report.detailsTitle": "Added automatically",
  "report.screenshotHint": "If you can, attach a screenshot to the e-mail as well.",
  "report.supportEmail": "Support e-mail",
  "report.copyEmail": "Copy the e-mail address",
  "report.emailCopied": "E-mail address copied.",
  "report.copied": "Copied. Paste it into an e-mail to {{email}}.",
  "report.copyFailed": "The browser did not allow copying. Select the text and copy it by hand.",
  "report.needDescription": "Write a few words in the box above first.",
  "report.webmail": "E-mail in the browser? Open it in",
  "report.copy": "Copy",
  "report.openEmail": "Open in e-mail",
  "report.close": "Close",
  "help.keyboardShortcuts": "Keyboard shortcuts",
  "help.reportBug": "Report a bug / Feedback",
  "help.contactSupport": "Contact support",
  "help.about": "About {{app}}",
  "account.signedInAs": "Signed in as",
  "account.account": "Account",
  "account.logout": "Logout",
  "login.platform": "MetEn Solutions Platform",
  "login.intro": "Sign in to access project workflows, engineering data, and team tools.",
  "login.email": "Email",
  "login.password": "Password",
  "login.showPassword": "Show password",
  "login.hidePassword": "Hide password",
  "login.submit": "Sign in",
  "login.secure": "Secure access for authorized MetEn users",
  "login.failed": "Login failed",
  "login.invalidCredentials": "Invalid login credentials",
  "login.emailNotConfirmed": "This email has not been confirmed yet. Check your inbox for the confirmation link.",
  "login.rateLimited": "Too many sign-in attempts. Try again in a few minutes.",
  "login.banned": "This account is suspended. Contact your administrator.",
  "login.invalidEmail": "Enter a valid email address.",
  "login.network": "The server could not be reached. Check your connection and try again.",
  "page.project.title": "Project Setup",
  "page.project.description": "Define the project identity, location, and core commercial assumptions.",
  "page.pv.title": "Photovoltaic System Setup",
  "page.pv.description": "Configure the photovoltaic system design, site assumptions, and supporting electrical parameters for project modeling.",
  "page.wind.title": "Wind Farm Setup",
  "page.wind.description": "Configure turbine selection, wind farm assumptions, explicit modeling settings, and optional custom turbine catalog entries.",
  "page.bess.title": "Battery Energy Storage System Setup",
  "page.bess.description": "Configure the battery energy storage system, core technical parameters, and operating strategy for project analysis.",
  "page.grid.title": "Grid Connection",
  "page.grid.description": "The point of interconnection in one place: connection level, contracted import/export limits, and the transformer topology with its loss modelling. These settings apply across PV, wind and battery assets.",
  "page.feasibility.general.title": "General",
  "page.feasibility.general.subtitle": "Client, Engineering, and Study Metadata.",
  "page.feasibility.weather.title": "Weather Data",
  "page.feasibility.weather.subtitle": "Choose the weather source that drives the feasibility study, and inspect what it contains. An imported TMY dataset replaces the platform's internal weather entirely for the asset it is assigned to.",
  "page.feasibility.technical.title": "Technical",
  "page.feasibility.technical.subtitle": "Plant (PV/BESS) and project inputs are configured in their respective pages. The feasibility characteristics shown here are read-only. Demand and tariff details are set on this page.",
  "page.feasibility.economics.title": "Economics",
  "page.feasibility.economics.subtitle": "Capture investment costs and financial parameters for the project.",
  "common.general": "General",
  "common.description": "Description",
  "common.name": "Name",
  "common.title": "Title",
  "common.email": "Email",
  "common.phone": "Phone",
  "common.address": "Address",
  "common.company": "Company",
  "common.notes": "Notes",
  "common.status": "Status",
  "common.type": "Type",
  "common.source": "Source",
  "common.date": "Date",
  "common.year": "Year",
  "common.month": "Month",
  "common.enabled": "Enabled",
  "common.disabled": "Disabled",
  "common.yes": "Yes",
  "common.no": "No",
  "common.loading": "Loading…",
  "common.required": "Required",
  "date.previous": "Previous",
  "date.next": "Next",
  "date.earlierYears": "Earlier years",
  "date.laterYears": "Later years",
  "date.chooseDate": "Choose date",
  "date.clear": "Clear",
  "date.today": "Today",
  "date.cancel": "Cancel",
  "date.ok": "OK",
};

// English is the source language. Keeping the Greek copy in one place makes
// wording reviewable by a native speaker and keeps domain terminology
// consistent between screens.
const EL = {
  "file.saved": "Το έργο αποθηκεύτηκε ✓",
  "file.savedAs": "Αποθηκεύτηκε ως «{{name}}» ✓",
  "file.deleted": "Το έργο διαγράφηκε",
  "file.discard": "Υπάρχουν μη αποθηκευμένες αλλαγές. Να απορριφθούν;",
  "file.discardImport": "Υπάρχουν μη αποθηκευμένες αλλαγές. Να απορριφθούν και να εισαχθεί αυτό το αρχείο έργου;",
  "file.deleteUnsaved": "Υπάρχουν μη αποθηκευμένες αλλαγές. Να διαγραφεί το έργο ούτως ή άλλως;",
  "file.confirmDelete": "Να διαγραφεί το έργο «{{name}}»; Η ενέργεια δεν μπορεί να αναιρεθεί.",
  "file.untitled": "Χωρίς τίτλο",
  "file.saveAsTitle": "Αποθήκευση ως",
  "file.newName": "Νέο όνομα έργου",
  "file.imported": "Εισήχθη το έργο «{{name}}». Χρησιμοποιήστε «Αποθήκευση ως» για να το αποθηκεύσετε στον λογαριασμό σας.",
  "file.loaded": "Το έργο φορτώθηκε",

  // Global chrome
  "language.name.en": "Αγγλικά",
  "language.name.el": "Ελληνικά",
  "language.switchToEnglish": "Μετάβαση στα Αγγλικά",
  "language.switchToGreek": "Μετάβαση στα Ελληνικά",
  "language.selector": "Γλώσσα εφαρμογής",
  "app.version": "έκδοση",
  "app.test": "ΔΟΚΙΜΗ",
  "app.project": "Έργο:",
  "app.daysLeft": "Ημέρες που απομένουν: {{count}}",
  "usage.title": "Χρήση λογαριασμού",
  "usage.projects": "Έργα {{used}}/{{limit}}",
  "usage.projectsNoLimit": "Έργα {{used}}",
  "usage.simulationsToday": "Προσομοιώσεις σήμερα {{used}}/{{limit}}",
  "usage.simulationsTodayNoLimit": "Προσομοιώσεις σήμερα {{used}}",
  "usage.optimizationsToday": "Βελτιστοποιήσεις σήμερα {{used}}/{{limit}}",
  "usage.optimizationsTodayNoLimit": "Βελτιστοποιήσεις σήμερα {{used}}",
  "usage.projectsHint": "Έργα αποθηκευμένα στον λογαριασμό σας. Η διαγραφή ενός έργου ελευθερώνει θέση.",
  "usage.projectsLimitHint": "Ο λογαριασμός σας επιτρέπει έως {{limit}} αποθηκευμένα έργα.",
  "usage.simulationsHint": "Προσομοιώσεις που έτρεξαν σήμερα. Όσες αποτυγχάνουν ή ακυρώνονται δεν μετρούν, και η μέτρηση ξεκινά από την αρχή κάθε μέρα.",
  "usage.simulationsLimitHint": "Ο λογαριασμός σας επιτρέπει έως {{limit}} προσομοιώσεις την ημέρα.",
  "usage.optimizationsHint": "Βελτιστοποιήσεις αγοράς που έτρεξαν σήμερα, με τον ίδιο τρόπο μέτρησης.",
  "usage.optimizationsLimitHint": "Ο λογαριασμός σας επιτρέπει έως {{limit}} βελτιστοποιήσεις την ημέρα.",
  "usage.limitReached": "Έχετε φτάσει σε ένα από τα όρια.",
  "usage.panel.subtitle": "Τι επιτρέπει ο λογαριασμός σας και πόσο από αυτό χρησιμοποιείται.",
  "usage.panel.projects": "Αποθηκευμένα έργα",
  "usage.panel.simulations": "Προσομοιώσεις σήμερα",
  "usage.panel.optimizations": "Βελτιστοποιήσεις αγοράς σήμερα",
  "usage.panel.ofLimit": "{{used}} από {{limit}}",
  "usage.panel.noLimit": "{{used}} · χωρίς όριο",
  "usage.panel.projectsLeft": "Μπορείτε να αποθηκεύσετε ακόμη {{count}}",
  "usage.panel.runsLeft": "Απομένουν {{count}} για σήμερα",
  "usage.panel.runsLeftOne": "Απομένει 1 για σήμερα",
  "usage.panel.full": "Το όριο συμπληρώθηκε",
  "usage.panel.projectsHint": "Η διαγραφή ενός έργου ελευθερώνει θέση.",
  "usage.panel.simulationsHint": "Όσες αποτυγχάνουν ή ακυρώνονται δεν μετρούν· η μέτρηση ξεκινά από την αρχή κάθε μέρα.",
  "usage.panel.optimizationsHint": "Μετρούν με τον ίδιο τρόπο όπως οι προσομοιώσεις.",
  "announcements.title": "Ειδοποιήσεις",
  "announcements.newOne": "1 νέα",
  "announcements.newMany": "{{count}} νέες",
  "announcements.newBadge": "Νέα",
  "announcements.empty": "Δεν υπάρχουν ειδοποιήσεις αυτή τη στιγμή.",
  "announcements.until": "Ισχύει έως {{time}} (ώρα Ελλάδος)",
  "announcements.kind.info": "Ενημέρωση",
  "announcements.kind.maintenance": "Συντήρηση",
  "announcements.kind.important": "Σημαντικό",

  // Top-level menus and navigation
  "menu.file": "Αρχείο",
  "menu.edit": "Επεξεργασία",
  "menu.view": "Προβολή",
  "menu.help": "Βοήθεια",
  "nav.menu": "Μενού",
  "nav.projectSetup": "Ρύθμιση Έργου",
  "nav.project": "Έργο",
  "nav.overviewAssets": "Επισκόπηση & Μονάδες",
  "nav.assetSetup": "Ρύθμιση Μονάδας",
  "nav.operationsDesk": "Κέντρο Λειτουργιών",
  "nav.trading": "Συναλλαγές",
  "nav.positions": "Θέσεις",
  "nav.orders": "Εντολές",
  "nav.reserves": "Εφεδρείες",
  "nav.optimisation": "Βελτιστοποίηση",
  "nav.passPlanner": "Προγραμματισμός Εκτελέσεων",
  "nav.reporting": "Αναφορές",
  "nav.settlementPerformance": "Εκκαθάριση & Απόδοση",
  "nav.inputsForecasts": "Είσοδοι & Προβλέψεις",
  "nav.setup": "Ρυθμίσεις",
  "nav.portfolioOnboarding": "Χαρτοφυλάκιο & Ένταξη",
  "nav.participationSettings": "Ρυθμίσεις Συμμετοχής",
  "nav.siteGeneral": "Τοποθεσία & Γενικά",
  "nav.pvSystem": "ΦΒ Σύστημα",
  "nav.windSystem": "Αιολικό Σύστημα",
  "nav.bess": "Αποθήκευση (BESS)",
  "nav.gridConnection": "Σύνδεση στο Δίκτυο",
  "nav.dataForecasts": "Δεδομένα & Προβλέψεις",
  "nav.weatherGeneration": "Καιρός & Παραγωγή",
  "nav.marketData": "Δεδομένα Αγοράς",
  "nav.marketOperations": "Λειτουργίες Αγοράς",
  "nav.operations": "Λειτουργίες",
  "nav.assetMonitoring": "Παρακολούθηση Μονάδων",
  "nav.monitoringPortfolio": "Χαρτοφυλάκιο",
  "nav.monitoringAlarms": "Συναγερμοί",
  "nav.monitoringReports": "Μηνιαία Αναφορά",
  "nav.monitoringSources": "Πηγές Δεδομένων",
  "nav.dailyOptimization": "Ημερήσια Βελτιστοποίηση",
  "nav.historicRuns": "Ιστορικές Εκτελέσεις",
  "nav.bidSchedules": "Προγράμματα Προσφορών",
  "nav.feasibilityStudy": "Μελέτη Σκοπιμότητας",
  "nav.studyInfo": "Στοιχεία Μελέτης",
  "nav.weatherInput": "Μετεωρολογικά Δεδομένα",
  "nav.technicalAssumptions": "Τεχνικές Παραδοχές",
  "nav.financialAssumptions": "Οικονομικές Παραδοχές",
  "nav.simulationReports": "Προσομοίωση & Αναφορές",
  "nav.thirdPartyNotices": "Επισημάνσεις Λογισμικού Τρίτων",

  // File / edit / view / help
  "action.new": "Νέο",
  "action.open": "Άνοιγμα…",
  "action.importProject": "Εισαγωγή Έργου…",
  "action.save": "Αποθήκευση",
  "action.saving": "Αποθήκευση…",
  "action.saveAs": "Αποθήκευση ως…",
  "action.exportProject": "Εξαγωγή Έργου",
  "action.exportProjectJson": "Εξαγωγή JSON Έργου",
  "action.deleteProject": "Διαγραφή έργου…",
  "action.undo": "Αναίρεση",
  "action.redo": "Επανάληψη",
  "action.cancel": "Ακύρωση",
  "action.close": "Κλείσιμο",
  "action.delete": "Διαγραφή",
  "action.search": "Αναζήτηση",
  "action.learnMore": "Μάθετε περισσότερα",
  "file.useSaveAs": "Χρησιμοποιήστε πρώτα την Αποθήκευση ως",
  "file.noChanges": "Δεν υπάρχουν αλλαγές",
  "file.noProject": "Δεν έχει ανοίξει έργο",
  "file.adminOnly": "Μόνο για διαχειριστές · χωρίς κρυπτογράφηση",
  "view.darkMode": "Σκοτεινή λειτουργία",
  "view.advancedOptions": "Σύνθετες επιλογές",
  "view.tooltips": "Επεξηγήσεις πεδίων",
  "help.documentation": "Τεκμηρίωση",
  "help.manualBtm": "Εγχειρίδιο: Πίσω από τον μετρητή (PDF)",
  "help.manualMerchant": "Εγχειρίδιο: Αγορά ενέργειας – Merchant (PDF)",
  "help.glossary": "Γλωσσάρι",
  "manualLink.label": "Οδηγός",
  "manualLink.tipBtm": "Άνοιγμα του εγχειριδίου χρήσης (πίσω από τον μετρητή) στη σελίδα {{page}}",
  "manualLink.tipMerchant": "Άνοιγμα του εγχειριδίου χρήσης (αγορά ενέργειας) στη σελίδα {{page}}",
  "glossary.search": "Αναζήτηση όρου",
  "glossary.placeholder": "π.χ. net billing, αυτοκατανάλωση, DSCR",
  "glossary.topics": "Θεματικές ενότητες",
  "glossary.group.all": "Όλοι",
  "glossary.group.btm": "Αυτοκατανάλωση",
  "glossary.group.pv": "Φ/Β και μπαταρία",
  "glossary.group.market": "Αγορά και δίκτυο",
  "glossary.group.finance": "Οικονομικά και χρηματοδότηση",
  "glossary.empty": "Δεν βρέθηκε όρος. Δοκιμάστε άλλη λέξη, στα ελληνικά ή στα αγγλικά.",
  "glossary.manualsHint": "Περισσότερα στα εγχειρίδια χρήσης (μενού Βοήθεια).",
  "glossary.close": "Κλείσιμο",
  "report.title": "Αναφορά προβλήματος / Σχόλια",
  "report.intro": "Περιγράψτε τι συνέβη. Τα στοιχεία που χρειαζόμαστε για να το εξετάσουμε προστίθενται αυτόματα παρακάτω, ώστε να βλέπετε ακριβώς τι θα σταλεί.",
  "report.kindLabel": "Είδος",
  "report.kind.problem": "Πρόβλημα",
  "report.kind.suggestion": "Πρόταση",
  "report.kind.question": "Ερώτηση",
  "report.descriptionLabel.problem": "Τι συνέβη;",
  "report.descriptionLabel.suggestion": "Τι θα θέλατε να υπάρχει;",
  "report.descriptionLabel.question": "Η ερώτησή σας",
  "report.descriptionPlaceholder.problem": "Τι κάνατε, τι περιμένατε να γίνει και τι έγινε τελικά.",
  "report.descriptionPlaceholder.suggestion": "Περιγράψτε την ιδέα και πώς θα βοηθούσε τη δουλειά σας.",
  "report.descriptionPlaceholder.question": "Γράψτε την ερώτησή σας όσο πιο αναλυτικά μπορείτε.",
  "report.detailsTitle": "Προστίθενται αυτόματα",
  "report.screenshotHint": "Αν μπορείτε, επισυνάψτε στο email και ένα στιγμιότυπο οθόνης.",
  "report.supportEmail": "Email υποστήριξης",
  "report.copyEmail": "Αντιγραφή της διεύθυνσης email",
  "report.emailCopied": "Η διεύθυνση email αντιγράφηκε.",
  "report.copied": "Αντιγράφηκε. Επικολλήστε το σε ένα email προς {{email}}.",
  "report.copyFailed": "Ο browser δεν επέτρεψε την αντιγραφή. Επιλέξτε το κείμενο και αντιγράψτε το χειροκίνητα.",
  "report.needDescription": "Γράψτε πρώτα λίγα λόγια στο πλαίσιο παραπάνω.",
  "report.webmail": "Email μέσα από τον browser; Άνοιγμα σε",
  "report.copy": "Αντιγραφή",
  "report.openEmail": "Άνοιγμα στο email",
  "report.close": "Κλείσιμο",
  "help.keyboardShortcuts": "Συντομεύσεις πληκτρολογίου",
  "help.reportBug": "Αναφορά σφάλματος / Σχόλια",
  "help.contactSupport": "Επικοινωνία με υποστήριξη",
  "help.about": "Σχετικά με το {{app}}",
  "account.signedInAs": "Συνδεδεμένος ως",
  "account.account": "Λογαριασμός",
  "account.logout": "Αποσύνδεση",

  // Authentication
  "login.platform": "Πλατφόρμα MetEn Solutions",
  "login.intro": "Συνδεθείτε για πρόσβαση στις ροές έργων, στα τεχνικά δεδομένα και στα εργαλεία της ομάδας.",
  "login.email": "Email",
  "login.password": "Κωδικός πρόσβασης",
  "login.showPassword": "Εμφάνιση κωδικού",
  "login.hidePassword": "Απόκρυψη κωδικού",
  "login.submit": "Σύνδεση",
  "login.secure": "Ασφαλής πρόσβαση για εξουσιοδοτημένους χρήστες της MetEn",
  "login.failed": "Η σύνδεση απέτυχε",
  "login.invalidCredentials": "Μη έγκυρα στοιχεία σύνδεσης",
  "login.emailNotConfirmed": "Το email δεν έχει επιβεβαιωθεί ακόμη. Ελέγξτε τα εισερχόμενά σας για τον σύνδεσμο επιβεβαίωσης.",
  "login.rateLimited": "Πολλές προσπάθειες σύνδεσης. Δοκιμάστε ξανά σε λίγα λεπτά.",
  "login.banned": "Ο λογαριασμός έχει ανασταλεί. Επικοινωνήστε με τον διαχειριστή.",
  "login.invalidEmail": "Εισαγάγετε μια έγκυρη διεύθυνση email.",
  "login.network": "Δεν ήταν δυνατή η επικοινωνία με τον διακομιστή. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.",

  // Common page headings
  "page.project.title": "Ρύθμιση Έργου",
  "page.project.description": "Ορίστε την ταυτότητα, την τοποθεσία και τις βασικές εμπορικές παραδοχές του έργου.",
  "page.pv.title": "Ρύθμιση Φωτοβολταϊκού Συστήματος",
  "page.pv.description": "Ρυθμίστε τον εξοπλισμό, τον προσανατολισμό και τις παραμέτρους υπολογισμού του φωτοβολταϊκού συστήματος.",
  "page.wind.title": "Ρύθμιση Αιολικού Πάρκου",
  "page.wind.description": "Διαμορφώστε την επιλογή ανεμογεννήτριας, τις παραδοχές του αιολικού πάρκου, τις ρυθμίσεις μοντελοποίησης και προαιρετικές καταχωρίσεις στον κατάλογο ανεμογεννητριών.",
  "page.bess.title": "Σύστημα Αποθήκευσης Ενέργειας με Μπαταρίες",
  "page.bess.description": "Διαμορφώστε το σύστημα αποθήκευσης με μπαταρίες, τις βασικές τεχνικές παραμέτρους και τη στρατηγική λειτουργίας για τη μελέτη του έργου.",
  "page.grid.title": "Σύνδεση στο Δίκτυο",
  "page.grid.description": "Ρυθμίστε το σημείο σύνδεσης με το δίκτυο: επίπεδο τάσης, συμβατικά όρια απορρόφησης και έγχυσης, διάταξη μετασχηματιστών και υπολογισμό απωλειών. Οι ρυθμίσεις αφορούν φωτοβολταϊκές και αιολικές μονάδες και μονάδες αποθήκευσης.",
  "page.feasibility.general.title": "Γενικά",
  "page.feasibility.general.subtitle": "Στοιχεία πελάτη, μηχανικού και μελέτης.",
  "page.feasibility.weather.title": "Μετεωρολογικά Δεδομένα",
  "page.feasibility.weather.subtitle": "Επιλέξτε τη μετεωρολογική πηγή που τροφοδοτεί τη μελέτη σκοπιμότητας και εξετάστε το περιεχόμενό της. Ένα εισαγόμενο σύνολο TMY αντικαθιστά πλήρως τα εσωτερικά μετεωρολογικά δεδομένα της πλατφόρμας για το αντίστοιχο στοιχείο.",
  "page.feasibility.technical.title": "Τεχνικά",
  "page.feasibility.technical.subtitle": "Οι είσοδοι της εγκατάστασης (Φ/Β και αποθήκευση) και του έργου ρυθμίζονται στις αντίστοιχες σελίδες. Τα χαρακτηριστικά σκοπιμότητας εδώ είναι μόνο για ανάγνωση. Τα στοιχεία ζήτησης και τιμολογίων ορίζονται σε αυτή τη σελίδα.",
  "page.feasibility.economics.title": "Οικονομικά",
  "page.feasibility.economics.subtitle": "Καταχωρίστε το επενδυτικό κόστος και τις οικονομικές παραμέτρους του έργου.",

  // Frequently reused form language
  "common.general": "Γενικά",
  "common.description": "Περιγραφή",
  "common.name": "Όνομα",
  "common.title": "Τίτλος",
  "common.email": "Email",
  "common.phone": "Τηλέφωνο",
  "common.address": "Διεύθυνση",
  "common.company": "Εταιρεία",
  "common.notes": "Σημειώσεις",
  "common.status": "Κατάσταση",
  "common.type": "Τύπος",
  "common.source": "Πηγή",
  "common.date": "Ημερομηνία",
  "common.year": "Έτος",
  "common.month": "Μήνας",
  "common.enabled": "Ενεργό",
  "common.disabled": "Ανενεργό",
  "common.yes": "Ναι",
  "common.no": "Όχι",
  "common.loading": "Φόρτωση…",
  "common.required": "Υποχρεωτικό",

  // Date picker
  "date.previous": "Προηγούμενο",
  "date.next": "Επόμενο",
  "date.earlierYears": "Προηγούμενα έτη",
  "date.laterYears": "Επόμενα έτη",
  "date.chooseDate": "Επιλογή ημερομηνίας",
  "date.clear": "Καθαρισμός",
  "date.today": "Σήμερα",
  "date.cancel": "Ακύρωση",
  "date.ok": "ΟΚ",
};

// Shared components often receive English copy as props. This bridge lets
// those components localize existing screens while new code uses semantic
// keys directly.
const ENGLISH_TEXT_KEYS = {
  "Overview & Assets": "nav.overviewAssets",
  "Asset Setup": "nav.assetSetup",
  "Operations Desk": "nav.operationsDesk",
  "Trading": "nav.trading",
  "Positions": "nav.positions",
  "Orders": "nav.orders",
  "Reserves": "nav.reserves",
  "Optimisation": "nav.optimisation",
  "Pass Planner": "nav.passPlanner",
  "Reporting": "nav.reporting",
  "Settlement & Performance": "nav.settlementPerformance",
  "Inputs & Forecasts": "nav.inputsForecasts",
  "Setup": "nav.setup",
  "Portfolio & Onboarding": "nav.portfolioOnboarding",
  "Participation Settings": "nav.participationSettings",
  "Data & Forecasts": "nav.dataForecasts",
  "Feasibility Study": "nav.feasibilityStudy",
  "Market Operations": "nav.marketOperations",
  "Asset Monitoring": "nav.assetMonitoring",
  "Portfolio": "nav.monitoringPortfolio",
  "Alarms": "nav.monitoringAlarms",
  "Monthly Report": "nav.monitoringReports",
  "Data Sources": "nav.monitoringSources",
  "Study Info": "nav.studyInfo",
  "Technical Assumptions": "nav.technicalAssumptions",
  "Financial Assumptions": "nav.financialAssumptions",
  "Simulation & Reports": "nav.simulationReports",
  "Daily Optimization": "nav.dailyOptimization",
  "Historic Runs": "nav.historicRuns",
  "Bid Schedules": "nav.bidSchedules",
  "Photovoltaic System Setup": "page.pv.title",
  "Configure the photovoltaic system design, site assumptions, and supporting electrical parameters for project modeling.": "page.pv.description",
  "Wind Farm Setup": "page.wind.title",
  "Configure turbine selection, wind farm assumptions, explicit modeling settings, and optional custom turbine catalog entries.": "page.wind.description",
  "Battery Energy Storage System Setup": "page.bess.title",
  "Configure the battery energy storage system, core technical parameters, and operating strategy for project analysis.": "page.bess.description",
  "Grid Connection": "page.grid.title",
  "The point of interconnection in one place: connection level, contracted import/export limits, and the transformer topology with its loss modelling. These settings apply across PV, wind and battery assets.": "page.grid.description",
  "Client, Engineering, and Study Metadata.": "page.feasibility.general.subtitle",
  "Weather Data": "page.feasibility.weather.title",
  "Choose the weather source that drives the feasibility study, and inspect what it contains. An imported TMY dataset replaces the platform's internal weather entirely for the asset it is assigned to.": "page.feasibility.weather.subtitle",
  Technical: "page.feasibility.technical.title",
  "Plant (PV/BESS) and project inputs are configured in their respective pages. The feasibility characteristics shown here are read-only. Demand and tariff details are set on this page.": "page.feasibility.technical.subtitle",
  Economics: "page.feasibility.economics.title",
  "Capture investment costs and financial parameters for the project.": "page.feasibility.economics.subtitle",
  General: "common.general",
  Description: "common.description",
  Name: "common.name",
  Title: "common.title",
  Email: "common.email",
  Phone: "common.phone",
  Address: "common.address",
  Company: "common.company",
  Notes: "common.notes",
  Status: "common.status",
  Type: "common.type",
  Source: "common.source",
  Date: "common.date",
  Year: "common.year",
  Month: "common.month",
  Yes: "common.yes",
  No: "common.no",
  Cancel: "action.cancel",
  Close: "action.close",
  Save: "action.save",
  Delete: "action.delete",
  Search: "action.search",
  "Learn more": "action.learnMore",
};

const LanguageContext = createContext(null);

const interpolate = (value, values = {}) =>
  String(value).replace(/\{\{(\w+)\}\}/g, (_, name) =>
    values[name] === undefined || values[name] === null ? "" : String(values[name])
  );

const initialLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === LANGUAGES.GREEK || stored === LANGUAGES.ENGLISH) return stored;
  } catch {
    // Storage may be unavailable in hardened/private browser modes.
  }
  return LANGUAGES.ENGLISH;
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(initialLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState(
      nextLanguage === LANGUAGES.GREEK ? LANGUAGES.GREEK : LANGUAGES.ENGLISH
    );
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((current) =>
      current === LANGUAGES.GREEK ? LANGUAGES.ENGLISH : LANGUAGES.GREEK
    );
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // The in-memory setting still works for the current session.
    }
    document.documentElement.lang = language;
    document.documentElement.dir = "ltr";
  }, [language]);

  const t = useCallback(
    (key, values) => {
      const source = language === LANGUAGES.GREEK ? EL[key] : EN[key];
      return interpolate(source || key, values);
    },
    [language]
  );

  // The legacy catalogue, for translateText below. Loaded on demand and only
  // in Greek, the same way LegacyLocalizationBridge loads it -- the bundler
  // memoises import(), so the two share one module instance.
  const [legacyCatalog, setLegacyCatalog] = useState(null);
  useEffect(() => {
    if (language !== LANGUAGES.GREEK || legacyCatalog) return undefined;
    let active = true;
    import("../i18n/legacyGreekCatalog.json").then((module) => {
      if (active) setLegacyCatalog(module.default || module);
    });
    return () => {
      active = false;
    };
  }, [language, legacyCatalog]);

  /**
   * Greek for a string a component has to translate ITSELF.
   *
   * Almost everything on screen is translated by LegacyLocalizationBridge,
   * which walks the DOM. But it can only reach text nodes and three
   * attributes, and an <input> keeps its text in the `value` PROPERTY - which
   * React owns and would overwrite anyway. So a read-only field displaying a
   * phrase ("Hybrid PV (PV + BESS)", "15 minutes (fixed)") sat in English
   * under a Greek label, and no catalogue entry could fix it: the entry was
   * there, nothing was looking it up.
   *
   * The curated page map is tried first, then the legacy catalogue, so this
   * returns the SAME Greek the bridge would have produced had the string been
   * a text node. Unknown strings come back unchanged, as before.
   */
  const translateText = useCallback(
    (text) => {
      if (language !== LANGUAGES.GREEK || typeof text !== "string") return text;
      const key = ENGLISH_TEXT_KEYS[text];
      if (key) return interpolate(EL[key] || text);
      // Curated translations (including tooltips) are available immediately;
      // the larger generated catalogue can finish loading afterwards.
      return translateLegacyText(text, legacyCatalog || {});
    },
    [language, legacyCatalog]
  );

  const value = useMemo(
    () => ({
      language,
      locale: language === LANGUAGES.GREEK ? "el-GR" : "en-GB",
      isGreek: language === LANGUAGES.GREEK,
      setLanguage,
      toggleLanguage,
      t,
      translateText,
    }),
    [language, setLanguage, t, toggleLanguage, translateText]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <LegacyLocalizationBridge language={language} />
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return context;
}

/**
 * translateText, for a component that only DISPLAYS a string.
 *
 * useLanguage throws outside the provider, which is right for anything that
 * reads or changes the language, and wrong for a leaf field that just wants
 * Greek for a label it is about to render: it turns "no provider in this
 * test" into a crash. This returns identity instead, so such a component
 * degrades to English rather than taking the tree down with it.
 */
export function useTranslateText() {
  const context = useContext(LanguageContext);
  return context?.translateText || ((text) => text);
}

/**
 * Whether the screen is in Greek, for a component that words its own sentences.
 *
 * A sentence carrying a name or a number ("“East roof” has no MPPT attached")
 * cannot be a catalogue key, so the component writes both languages itself.
 * Non-throwing for the same reason as useTranslateText: outside the provider -
 * a unit test - it is simply English.
 */
export function useIsGreek() {
  const context = useContext(LanguageContext);
  return Boolean(context?.isGreek);
}

export const languageStorageKey = STORAGE_KEY;
